import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchProducts, deleteProduct, queueImportProducts, processImportQueue, fetchClickStats, updateProduct, refreshProductImage, getSession, logout, fetchImportFallbackDiagnostics } from "@/lib/api";
import { parseCSV } from "@/lib/csv-parser";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Trash2, LogOut, Package, MousePointerClick, Store, BarChart3, Home, Pencil, RefreshCcw, ChevronLeft, ChevronRight, Search, ArrowUpDown, ChevronUp, ChevronDown, Clock3, CalendarDays, TrendingUp, DollarSign } from "lucide-react";
import { EditProductDialog } from "@/components/EditProductDialog";
import { SeoHead } from "@/components/SeoHead";
import type { AuthSession, ImportFallbackDiagnostics, Product } from "@/lib/api";

type FilterMode = "all" | "yes" | "no";
type SortKey = "created_at" | "item_name" | "price" | "shop_name" | "clicks" | "commission_rate" | "featured" | "banner" | "national";
type SortDirection = "asc" | "desc";
const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"] as const;

const Admin = () => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [refreshingImageIds, setRefreshingImageIds] = useState<string[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [tab, setTab] = useState<"products" | "stats">("products");
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [shopFilter, setShopFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState<FilterMode>("all");
  const [bannerFilter, setBannerFilter] = useState<FilterMode>("all");
  const [nationalFilter, setNationalFilter] = useState<FilterMode>("all");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [pageSize, setPageSize] = useState("25");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    getSession().then((currentSession) => {
      setSession(currentSession);
      setLoading(false);
      if (!currentSession) navigate("/login");
    }).catch(() => {
      setLoading(false);
      navigate("/login");
    });
  }, [navigate]);

  const { data: productsData } = useQuery({ queryKey: ["products"], queryFn: () => fetchProducts(), enabled: !!session });
  const { data: clicksData } = useQuery({ queryKey: ["clicks"], queryFn: fetchClickStats, enabled: !!session });
  const { data: diagnosticsData } = useQuery({
    queryKey: ["import-fallback-diagnostics"],
    queryFn: fetchImportFallbackDiagnostics,
    enabled: !!session,
    staleTime: 60_000,
  });
  const products = Array.isArray(productsData) ? productsData : [];
  const clicks = Array.isArray(clicksData) ? clicksData : [];
  const diagnostics: ImportFallbackDiagnostics | null = diagnosticsData ?? null;
  const importFallbackReady = !!(
    diagnostics?.shell_exec_available &&
    diagnostics?.script_exists &&
    diagnostics?.node_version?.ok &&
    diagnostics?.playwright_check?.ok
  );

  const clickCountByProduct = useMemo(() => clicks.reduce<Record<string, number>>((acc, click) => {
    acc[click.product_id] = (acc[click.product_id] || 0) + 1;
    return acc;
  }, {}), [clicks]);

  const clicksByProduct = useMemo(() => clicks.reduce<Record<string, { name: string; count: number }>>((acc, click) => {
    const name = click?.products?.item_name ?? "Desconhecido";
    if (!acc[click.product_id]) acc[click.product_id] = { name, count: 0 };
    acc[click.product_id].count++;
    return acc;
  }, {}), [clicks]);

  const clicksByShop = useMemo(() => clicks.reduce<Record<string, number>>((acc, click) => {
    const shop = click?.products?.shop_name ?? "Desconhecido";
    acc[shop] = (acc[shop] || 0) + 1;
    return acc;
  }, {}), [clicks]);

  const productById = useMemo(() => products.reduce<Record<string, Product>>((acc, product) => {
    acc[product.id] = product;
    return acc;
  }, {}), [products]);

  const formatDateTime = useCallback((value: string) => new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value)), []);

  const formatMonth = useCallback((value: string) => new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date(value)), []);

  const parseCurrencyValue = useCallback((value?: string | null) => {
    if (!value) return 0;
    const normalized = String(value).replace(/[^\d,.-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }, []);

  const lastClick = useMemo(() => {
    if (clicks.length === 0) return null;
    return [...clicks].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }, [clicks]);

  const clicksByDay = useMemo(() => {
    return Object.entries(clicks.reduce<Record<string, number>>((acc, click) => {
      const dayKey = click.created_at.slice(0, 10);
      acc[dayKey] = (acc[dayKey] || 0) + 1;
      return acc;
    }, {}))
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 10);
  }, [clicks]);

  const clicksByMonth = useMemo(() => {
    return Object.entries(clicks.reduce<Record<string, number>>((acc, click) => {
      const monthKey = click.created_at.slice(0, 7);
      acc[monthKey] = (acc[monthKey] || 0) + 1;
      return acc;
    }, {}))
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 12);
  }, [clicks]);

  const clicksByYear = useMemo(() => {
    return Object.entries(clicks.reduce<Record<string, number>>((acc, click) => {
      const yearKey = click.created_at.slice(0, 4);
      acc[yearKey] = (acc[yearKey] || 0) + 1;
      return acc;
    }, {})).sort(([a], [b]) => b.localeCompare(a));
  }, [clicks]);

  const recentClicks = useMemo(() => {
    return [...clicks]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 12);
  }, [clicks]);

  const estimatedProfitByProduct = useMemo(() => {
    return Object.entries(clickCountByProduct)
      .map(([productId, count]) => {
        const product = productById[productId];
        const commissionValue = parseCurrencyValue(product?.commission);
        return {
          productId,
          name: product?.item_name ?? clicksByProduct[productId]?.name ?? "Desconhecido",
          clicks: count,
          commissionValue,
          estimatedRevenue: commissionValue * count,
        };
      })
      .sort((a, b) => b.estimatedRevenue - a.estimatedRevenue)
      .slice(0, 10);
  }, [clickCountByProduct, clicksByProduct, parseCurrencyValue, productById]);

  const mostProfitableProduct = estimatedProfitByProduct[0] ?? null;

  const shops = useMemo(() => [...new Set(products.map((product) => product.shop_name).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b)), [products]);

  const filteredProducts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return products
      .filter((product) => {
        if (shopFilter !== "all" && (product.shop_name ?? "") !== shopFilter) return false;
        if (featuredFilter !== "all" && product.featured !== (featuredFilter === "yes")) return false;
        if (bannerFilter !== "all" && product.banner !== (bannerFilter === "yes")) return false;
        if (nationalFilter !== "all" && product.national !== (nationalFilter === "yes")) return false;
        if (!search) return true;
        const haystack = [product.item_name, product.item_id, product.shop_name, product.category, product.subcategory].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(search);
      })
      .sort((a, b) => {
        const direction = sortDirection === "asc" ? 1 : -1;
        if (sortKey === "item_name") return a.item_name.localeCompare(b.item_name, "pt-BR") * direction;
        if (sortKey === "shop_name") return (a.shop_name ?? "").localeCompare(b.shop_name ?? "", "pt-BR") * direction;
        if (sortKey === "clicks") return ((clickCountByProduct[a.id] || 0) - (clickCountByProduct[b.id] || 0)) * direction;
        if (sortKey === "price") {
          const pa = Number(String(a.price).replace(/[^\d,.-]/g, "").replace(".", "").replace(",", "."));
          const pb = Number(String(b.price).replace(/[^\d,.-]/g, "").replace(".", "").replace(",", "."));
          return (pa - pb) * direction;
        }
        if (sortKey === "commission_rate") {
          const ca = Number(String(a.commission_rate ?? "").replace(/[^\d,.-]/g, "").replace(",", "."));
          const cb = Number(String(b.commission_rate ?? "").replace(/[^\d,.-]/g, "").replace(",", "."));
          return (ca - cb) * direction;
        }
        if (sortKey === "featured") return ((a.featured ? 1 : 0) - (b.featured ? 1 : 0)) * direction;
        if (sortKey === "banner") return ((a.banner ? 1 : 0) - (b.banner ? 1 : 0)) * direction;
        if (sortKey === "national") return ((a.national ? 1 : 0) - (b.national ? 1 : 0)) * direction;
        return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * direction;
      });
  }, [bannerFilter, clickCountByProduct, featuredFilter, nationalFilter, products, searchTerm, shopFilter, sortDirection, sortKey]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, shopFilter, featuredFilter, bannerFilter, nationalFilter, sortDirection, sortKey, pageSize]);
  useEffect(() => {
    const ids = new Set(products.map((product) => product.id));
    setSelectedProductIds((prev) => prev.filter((id) => ids.has(id)));
  }, [products]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSizeNumber));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedProducts = filteredProducts.slice((safeCurrentPage - 1) * pageSizeNumber, safeCurrentPage * pageSizeNumber);
  const currentPageIds = paginatedProducts.map((product) => product.id);
  const allFilteredIds = filteredProducts.map((product) => product.id);
  const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedProductIds.includes(id));
  const someCurrentPageSelected = currentPageIds.some((id) => selectedProductIds.includes(id));

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const parsed = parseCSV(await file.text());
      if (parsed.length === 0) {
        toast.error("Nenhum produto encontrado no arquivo");
        return;
      }
      setImportStatus(`Enfileirando ${parsed.length} produtos...`);
      const queueResult = await queueImportProducts(parsed);
      if (!queueResult || typeof queueResult !== "object") {
        toast.error("Erro ao importar: resposta inválida do servidor");
        return;
      }
      let inserted = 0, updated = 0, errors = 0, processed = 0, remaining = queueResult.queued;
      setImportStatus(`Produtos enfileirados: ${queueResult.queued}/${queueResult.total}. Processando fila...`);
      while (remaining > 0) {
        const result = await processImportQueue(5);
        if (!result || typeof result !== "object") {
          toast.error("Erro ao processar a fila de importação");
          return;
        }
        inserted += result.inserted;
        updated += result.updated;
        errors += result.errors;
        processed += result.processed;
        remaining = result.remaining;
        const progressPercent = Math.round(((result.total - remaining) / result.total) * 100);
        setImportStatus(`Processando fila: ${processed}/${result.total} (${progressPercent}%) - inseridos ${inserted}, atualizados ${updated}, erros ${errors}. Restam ${remaining}.`);
      }
      setImporting(false);
      setImportStatus(`Importação concluída! Total processado: ${processed} produtos (inseridos: ${inserted}, atualizados: ${updated}, erros: ${errors})`);
      toast.success(`Importação concluída! ${inserted} inseridos, ${updated} atualizados`);
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch (error) {
      setImporting(false);
      setImportStatus(`Erro durante a importação: ${error instanceof Error ? error.message : "Erro desconhecido"}`);
      toast.error("Erro durante a importação");
    }
  }, [queryClient]);

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja remover este produto?")) return;
    try {
      await deleteProduct(id);
      toast.success("Produto removido");
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch {
      toast.error("Erro ao remover produto");
    }
  };

  const handleRefreshImage = async (id: string) => {
    setRefreshingImageIds((prev) => [...prev, id]);
    try {
      const result = await refreshProductImage(id);
      toast.success(result?.image_url ? "Imagem atualizada com sucesso" : "Atualização concluída");
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch (error) {
      toast.error(`Falha ao atualizar imagem: ${error instanceof Error ? error.message : "Erro desconhecido"}`);
    } finally {
      setRefreshingImageIds((prev) => prev.filter((currentId) => currentId !== id));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleSelection = (id: string, checked: boolean) => {
    setSelectedProductIds((prev) => checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((currentId) => currentId !== id));
  };

  const toggleCurrentPageSelection = (checked: boolean) => {
    setSelectedProductIds((prev) => checked ? [...new Set([...prev, ...currentPageIds])] : prev.filter((id) => !currentPageIds.includes(id)));
  };

  const runBulkUpdate = async (changes: Partial<Product>, successMessage: string) => {
    if (selectedProductIds.length === 0) {
      toast.error("Selecione ao menos um produto");
      return;
    }
    setBulkUpdating(true);
    try {
      await Promise.all(selectedProductIds.map((id) => updateProduct(id, changes)));
      toast.success(successMessage);
      setSelectedProductIds([]);
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch {
      toast.error("Não foi possível atualizar os produtos selecionados");
    } finally {
      setBulkUpdating(false);
    }
  };

  const toggleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => prev === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === "item_name" || nextKey === "shop_name" ? "asc" : "desc");
  };

  const renderSortButton = (label: string, key: SortKey) => {
    const isActive = sortKey === key;
    const Icon = !isActive ? ArrowUpDown : sortDirection === "asc" ? ChevronUp : ChevronDown;
    return (
      <button type="button" onClick={() => toggleSort(key)} className="inline-flex items-center gap-1 font-medium text-left hover:text-foreground">
        <span>{label}</span>
        <Icon className={`h-4 w-4 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
      </button>
    );
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center">Carregando...</div>;

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Painel administrativo"
        description="Gerencie produtos, importacoes e relatorios da NetLand Shop."
        canonicalPath="/admin"
        robots="noindex,nofollow"
      />
      <header className="border-b bg-card">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <h1 className="text-xl font-bold">Painel Administrativo</h1>
          <div className="flex gap-2">
            <Link to="/"><Button variant="outline" size="sm" className="gap-1"><Home className="h-4 w-4" /> Vitrine</Button></Link>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-1"><LogOut className="h-4 w-4" /> Sair</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto space-y-6 px-4 py-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <Card><CardContent className="flex items-center gap-4 p-6"><Package className="h-10 w-10 text-primary" /><div><p className="text-sm text-muted-foreground">Produtos</p><p className="text-2xl font-bold">{products.length}</p></div></CardContent></Card>
          <Card><CardContent className="flex items-center gap-4 p-6"><MousePointerClick className="h-10 w-10 text-chart-2" /><div><p className="text-sm text-muted-foreground">Total de Cliques</p><p className="text-2xl font-bold">{clicks.length}</p></div></CardContent></Card>
          <Card><CardContent className="flex items-center gap-4 p-6"><Store className="h-10 w-10 text-success" /><div><p className="text-sm text-muted-foreground">Lojas</p><p className="text-2xl font-bold">{shops.length}</p></div></CardContent></Card>
        </div>

        <div className="flex gap-2">
          <Button variant={tab === "products" ? "default" : "outline"} onClick={() => setTab("products")} className="gap-2"><Package className="h-4 w-4" /> Produtos</Button>
          <Button variant={tab === "stats" ? "default" : "outline"} onClick={() => setTab("stats")} className="gap-2"><BarChart3 className="h-4 w-4" /> Relatórios</Button>
        </div>

        {tab === "products" && (
          <>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Importar Produtos (CSV)</CardTitle></CardHeader>
              <CardContent>
                <Input type="file" accept=".csv,.txt" onChange={handleFileUpload} disabled={importing} />
                {importStatus && <div className="mt-3 rounded-lg border bg-muted/50 p-4"><p className={`text-sm font-medium ${importing ? "text-blue-600" : "text-green-600"}`}>{importStatus}</p></div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="space-y-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <CardTitle>Produtos</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{filteredProducts.length} filtrados</Badge>
                    <Badge variant="outline">{selectedProductIds.length} selecionados</Badge>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
                  <div className="relative md:col-span-2 xl:col-span-2">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nome, ID, loja ou categoria..." className="pl-9" />
                  </div>
                  <Select value={shopFilter} onValueChange={setShopFilter}><SelectTrigger><SelectValue placeholder="Loja" /></SelectTrigger><SelectContent><SelectItem value="all">Todas as lojas</SelectItem>{shops.map((shop) => <SelectItem key={shop} value={shop}>{shop}</SelectItem>)}</SelectContent></Select>
                  <Select value={featuredFilter} onValueChange={(value) => setFeaturedFilter(value as FilterMode)}><SelectTrigger><SelectValue placeholder="Destaque" /></SelectTrigger><SelectContent><SelectItem value="all">Destaque: todos</SelectItem><SelectItem value="yes">Só em destaque</SelectItem><SelectItem value="no">Só sem destaque</SelectItem></SelectContent></Select>
                  <Select value={bannerFilter} onValueChange={(value) => setBannerFilter(value as FilterMode)}><SelectTrigger><SelectValue placeholder="Banner" /></SelectTrigger><SelectContent><SelectItem value="all">Banner: todos</SelectItem><SelectItem value="yes">Só no banner</SelectItem><SelectItem value="no">Só fora do banner</SelectItem></SelectContent></Select>
                  <Select value={nationalFilter} onValueChange={(value) => setNationalFilter(value as FilterMode)}><SelectTrigger><SelectValue placeholder="Nacional" /></SelectTrigger><SelectContent><SelectItem value="all">Nacional: todos</SelectItem><SelectItem value="yes">Só nacionais</SelectItem><SelectItem value="no">Só importados</SelectItem></SelectContent></Select>
                </div>

                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                  <Select value={pageSize} onValueChange={setPageSize}><SelectTrigger><SelectValue placeholder="Página" /></SelectTrigger><SelectContent>{PAGE_SIZE_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option} por página</SelectItem>)}</SelectContent></Select>
                  <div className="flex flex-wrap gap-2 md:col-span-2 xl:col-span-5">
                    <Button variant="outline" size="sm" onClick={() => setSelectedProductIds(allFilteredIds)}>Marcar todos filtrados</Button>
                    <Button variant="outline" size="sm" onClick={() => setSelectedProductIds([])}>Limpar seleção</Button>
                    <Button size="sm" variant="secondary" disabled={bulkUpdating || selectedProductIds.length === 0} onClick={() => runBulkUpdate({ featured: true }, "Produtos marcados como destaque")}>Destacar selecionados</Button>
                    <Button size="sm" variant="secondary" disabled={bulkUpdating || selectedProductIds.length === 0} onClick={() => runBulkUpdate({ banner: true }, "Produtos marcados no banner")}>Banner nos selecionados</Button>
                    <Button size="sm" variant="secondary" disabled={bulkUpdating || selectedProductIds.length === 0} onClick={() => runBulkUpdate({ national: true }, "Produtos marcados como nacionais")}>Nacional nos selecionados</Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"><Checkbox checked={allCurrentPageSelected ? true : someCurrentPageSelected ? "indeterminate" : false} onCheckedChange={(checked) => toggleCurrentPageSelection(checked === true)} aria-label="Selecionar produtos da página" /></TableHead>
                      <TableHead className="w-12">Img</TableHead>
                      <TableHead>{renderSortButton("Nome", "item_name")}</TableHead>
                      <TableHead>{renderSortButton("Preço", "price")}</TableHead>
                      <TableHead>{renderSortButton("Loja", "shop_name")}</TableHead>
                      <TableHead>{renderSortButton("Cliques", "clicks")}</TableHead>
                      <TableHead>{renderSortButton("Comissão", "commission_rate")}</TableHead>
                      <TableHead>{renderSortButton("Destaque", "featured")}</TableHead>
                      <TableHead>{renderSortButton("Banner", "banner")}</TableHead>
                      <TableHead>{renderSortButton("Nacional", "national")}</TableHead>
                      <TableHead className="w-24">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProducts.length === 0 && <TableRow><TableCell colSpan={11} className="py-10 text-center text-muted-foreground">Nenhum produto encontrado para os filtros atuais.</TableCell></TableRow>}
                    {paginatedProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell><Checkbox checked={selectedProductIds.includes(product.id)} onCheckedChange={(checked) => toggleSelection(product.id, checked === true)} aria-label={`Selecionar ${product.item_name}`} /></TableCell>
                        <TableCell className="p-1">{product.image_url ? <img src={product.image_url} alt="" className="h-10 w-10 rounded object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span className="flex h-10 w-10 items-center justify-center rounded bg-muted text-lg">📦</span>}</TableCell>
                        <TableCell className="max-w-xs"><div className="flex flex-col gap-1"><span className="truncate font-medium">{product.item_name}</span><span className="text-xs text-muted-foreground">ID: {product.item_id}</span></div></TableCell>
                        <TableCell>{product.price}</TableCell>
                        <TableCell>{product.shop_name || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="min-w-12 justify-center">{clickCountByProduct[product.id] || 0}</Badge></TableCell>
                        <TableCell>{product.commission_rate || "—"}</TableCell>
                        <TableCell className="text-center"><input type="checkbox" checked={product.featured} onChange={async (e) => { await updateProduct(product.id, { featured: e.target.checked }); queryClient.invalidateQueries({ queryKey: ["products"] }); }} className="h-4 w-4 cursor-pointer accent-primary" /></TableCell>
                        <TableCell className="text-center"><input type="checkbox" checked={product.banner} onChange={async (e) => { await updateProduct(product.id, { banner: e.target.checked }); queryClient.invalidateQueries({ queryKey: ["products"] }); }} className="h-4 w-4 cursor-pointer accent-primary" /></TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {product.national && <span className="inline-flex h-6 items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 text-xs font-semibold text-emerald-700" title="Produto Nacional">BR</span>}
                            <input type="checkbox" checked={product.national} onChange={async (e) => { await updateProduct(product.id, { national: e.target.checked }); queryClient.invalidateQueries({ queryKey: ["products"] }); }} className="h-4 w-4 cursor-pointer accent-emerald-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setEditingProduct(product)} title="Editar produto"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                            {!product.image_url && <Button variant="ghost" size="icon" onClick={() => handleRefreshImage(product.id)} disabled={refreshingImageIds.includes(product.id)} title={refreshingImageIds.includes(product.id) ? "Atualizando imagem..." : "Atualizar imagem"}>{refreshingImageIds.includes(product.id) ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" /> : <RefreshCcw className="h-4 w-4 text-primary" />}</Button>}
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm text-muted-foreground">Página {safeCurrentPage} de {totalPages} • mostrando {paginatedProducts.length} de {filteredProducts.length} produtos</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={safeCurrentPage <= 1} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}><ChevronLeft className="h-4 w-4" /> Anterior</Button>
                    <Button variant="outline" size="sm" disabled={safeCurrentPage >= totalPages} onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>Próxima <ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {tab === "products" && (
          <Card>
            <CardHeader>
              <CardTitle>Diagnostico do import no servidor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={importFallbackReady ? "default" : "destructive"}>
                  {importFallbackReady ? "Fallback pronto" : "Fallback incompleto"}
                </Badge>
                <Badge variant={diagnostics?.script_exists ? "secondary" : "destructive"}>
                  Script: {diagnostics?.script_exists ? "OK" : "ausente"}
                </Badge>
                <Badge variant={diagnostics?.shell_exec_available ? "secondary" : "destructive"}>
                  shell_exec: {diagnostics?.shell_exec_available ? "OK" : "bloqueado"}
                </Badge>
                <Badge variant={diagnostics?.node_version?.ok ? "secondary" : "destructive"}>
                  Node: {diagnostics?.node_version?.ok ? "OK" : "ausente"}
                </Badge>
                <Badge variant={diagnostics?.playwright_check?.ok ? "secondary" : "destructive"}>
                  Playwright: {diagnostics?.playwright_check?.ok ? "OK" : "indisponivel"}
                </Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <p className="text-sm font-medium text-foreground">Leitura rapida para cPanel</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {importFallbackReady
                      ? "O fallback com browser esta pronto para ajudar quando a captura padrao nao encontrar imagens."
                      : "No seu cPanel ainda falta ambiente para o fallback com browser. O import normal pode funcionar, mas o fallback avancado ainda nao esta operacional."}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm font-medium text-foreground">Retorno do Node</p>
                  <p className="mt-2 break-all text-sm text-muted-foreground">
                    {diagnostics?.node_version?.output || "Sem retorno do diagnostico."}
                  </p>
                </div>
              </div>

              {diagnostics?.script_path && (
                <div className="rounded-lg border p-4">
                  <p className="text-sm font-medium text-foreground">Script encontrado</p>
                  <p className="mt-2 break-all text-sm text-muted-foreground">{diagnostics.script_path}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "stats" && (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <Clock3 className="h-10 w-10 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Último Clique</p>
                    <p className="text-sm font-semibold">{lastClick ? formatDateTime(lastClick.created_at) : "Sem cliques"}</p>
                    <p className="text-xs text-muted-foreground">{lastClick?.products?.item_name ?? "—"}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <CalendarDays className="h-10 w-10 text-chart-2" />
                  <div>
                    <p className="text-sm text-muted-foreground">Dias com Cliques</p>
                    <p className="text-2xl font-bold">{clicksByDay.length}</p>
                    <p className="text-xs text-muted-foreground">{clicksByMonth.length} meses com atividade</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <TrendingUp className="h-10 w-10 text-success" />
                  <div>
                    <p className="text-sm text-muted-foreground">Produto Mais Clicado</p>
                    <p className="text-sm font-semibold">{Object.values(clicksByProduct).sort((a, b) => b.count - a.count)[0]?.name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">{Object.values(clicksByProduct).sort((a, b) => b.count - a.count)[0]?.count ?? 0} cliques</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <DollarSign className="h-10 w-10 text-emerald-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Produto Mais Rentável</p>
                    <p className="text-sm font-semibold">{mostProfitableProduct?.name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {mostProfitableProduct
                        ? `${mostProfitableProduct.clicks} cliques • R$ ${mostProfitableProduct.estimatedRevenue.toFixed(2)} estimados`
                        : "Sem base de comissão"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>Cliques por Dia</CardTitle></CardHeader>
                <CardContent>
                  {clicksByDay.map(([day, count]) => (
                    <div key={day} className="flex items-center justify-between border-b py-2 last:border-0">
                      <span className="text-sm">{new Date(`${day}T00:00:00`).toLocaleDateString("pt-BR")}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {clicksByDay.length === 0 && <p className="text-sm text-muted-foreground">Nenhum clique registrado</p>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Cliques por Mês e Ano</CardTitle></CardHeader>
                <CardContent>
                  {clicksByMonth.map(([month, count]) => (
                    <div key={month} className="flex items-center justify-between border-b py-2 last:border-0">
                      <span className="text-sm capitalize">{formatMonth(`${month}-01T00:00:00`)}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {clicksByMonth.length === 0 && <p className="text-sm text-muted-foreground">Nenhum clique registrado</p>}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 xl:grid-cols-3">
              <Card>
                <CardHeader><CardTitle>Cliques por Produto</CardTitle></CardHeader>
                <CardContent>
                  {Object.entries(clicksByProduct).sort(([, a], [, b]) => b.count - a.count).slice(0, 10).map(([id, { name, count }]) => (
                    <div key={id} className="flex items-center justify-between border-b py-2 last:border-0">
                      <span className="max-w-xs truncate text-sm">{name}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {Object.keys(clicksByProduct).length === 0 && <p className="text-sm text-muted-foreground">Nenhum clique registrado</p>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Cliques por Loja</CardTitle></CardHeader>
                <CardContent>
                  {Object.entries(clicksByShop).sort(([, a], [, b]) => b - a).slice(0, 10).map(([shop, count]) => (
                    <div key={shop} className="flex items-center justify-between border-b py-2 last:border-0">
                      <span className="text-sm">{shop}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {Object.keys(clicksByShop).length === 0 && <p className="text-sm text-muted-foreground">Nenhum clique registrado</p>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Rentabilidade Estimada</CardTitle></CardHeader>
                <CardContent>
                  {estimatedProfitByProduct.map((product) => (
                    <div key={product.productId} className="flex items-center justify-between border-b py-2 last:border-0">
                      <div className="max-w-xs">
                        <p className="truncate text-sm">{product.name}</p>
                        <p className="text-xs text-muted-foreground">Comissão: R$ {product.commissionValue.toFixed(2)} • {product.clicks} cliques</p>
                      </div>
                      <span className="font-bold text-emerald-600">R$ {product.estimatedRevenue.toFixed(2)}</span>
                    </div>
                  ))}
                  {estimatedProfitByProduct.length === 0 && <p className="text-sm text-muted-foreground">Sem comissão suficiente para estimar rentabilidade</p>}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle>Cliques Recentes</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead>Loja</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Data e Hora</TableHead>
                      <TableHead>Dia</TableHead>
                      <TableHead>Mês</TableHead>
                      <TableHead>Ano</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentClicks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                          Nenhum clique registrado.
                        </TableCell>
                      </TableRow>
                    )}
                    {recentClicks.map((click) => {
                      const clickDate = new Date(click.created_at);
                      return (
                        <TableRow key={click.id}>
                          <TableCell className="max-w-xs truncate">{click.products?.item_name ?? productById[click.product_id]?.item_name ?? "Desconhecido"}</TableCell>
                          <TableCell>{click.products?.shop_name ?? productById[click.product_id]?.shop_name ?? "—"}</TableCell>
                          <TableCell>{click.link_type}</TableCell>
                          <TableCell>{formatDateTime(click.created_at)}</TableCell>
                          <TableCell>{clickDate.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })}</TableCell>
                          <TableCell className="capitalize">{formatMonth(click.created_at)}</TableCell>
                          <TableCell>{clickDate.getFullYear()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Cliques por Ano</CardTitle></CardHeader>
              <CardContent>
                {clicksByYear.map(([year, count]) => (
                  <div key={year} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{year}</span>
                    <span className="font-bold text-primary">{count}</span>
                  </div>
                ))}
                {clicksByYear.length === 0 && <p className="text-sm text-muted-foreground">Nenhum clique registrado</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {editingProduct && <EditProductDialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)} product={editingProduct} />}
    </div>
  );
};

export default Admin;
